/*
 * AssistantWindow — the edge assistant as its own window.
 *
 * This renders INSTEAD of the dashboard (see index.js), not inside it: opened
 * via ?view=assistant&node=<id>, it has no sidebar, no node tabs and no
 * dashboard chrome. A query param rather than a path so it works unchanged
 * under any PUBLIC_URL — the multi-node build serves from /, the on-device
 * console from /console/ — with no nginx or router config.
 *
 * The two behaviours that matter are the same ones the panel had:
 *
 *  1. The agent cannot change anything. Mutating tools queue a proposal; the
 *     change happens only when the operator presses Apply here, which issues
 *     the write from this browser on the operator's own session — exactly what
 *     the equivalent dashboard button does.
 *  2. Apply replays the proposal's own method/path/body verbatim. This window
 *     never reinterprets what the agent asked for, and a failed write leaves
 *     the proposal pending rather than marking it applied.
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  FiArrowUp, FiTool, FiCheck, FiX, FiRefreshCw, FiAlertTriangle,
  FiChevronDown, FiChevronRight, FiCompass, FiExternalLink, FiSquare,
  FiPlus, FiTrash2, FiMessageSquare, FiSidebar, FiRotateCcw,
} from "react-icons/fi";
import DiagramCard from "./DiagramCard";
import TieredAnswer from "./TieredAnswer";
import TourCard from "./TourCard";
import AdaIntro from "./AdaIntro";
import { renderMarkdownish } from "./markdownish";
import { stepLabel } from "./stepLabel";
import { useHistory, relativeTime, loadConversation, readActive, writeActive } from "./HistoryPane";
import useVisibleInterval from "../hooks/useVisibleInterval";
import { nodeApiUrlFor, IS_SINGLE_EDGE, serverApiUrl } from "../serverApi";
import { changeTitle, describeChange, humanLabel, humanValue } from "../tour/proposalCopy";
import { buildTour, orderChanges, stepTitle } from "../tour/tourSteps";
import {
  assignArtifacts, beginScope, claim, emptyScope, isDrawingCall, isProposalCall,
  orphanPending, ownedElsewhere, restoreScope, storedScope, withPrior,
} from "../tour/proposalMatch";
import TourProgress, { restartAndOpen, resumeAndOpen } from "../tour/TourProgress";
import { navLabel } from "../navigation";
import { useSession } from "./PortalGate";
import {
  startTour, takeApplied, takeTourQuestion, endTour, pauseTour, useTour,
  walkthroughOf, endingOf,
} from "../tour/tourStore";
import {
  ASSISTANT_NAME, CONSOLE_NAME, VIEW_CONSOLE, VIEW_LANDING, navigateTo, readNode,
} from "../appView";
import { confirmChange } from "../utils/confirmChange";

// Each conversation gets its own ID so the edge keeps their message windows
// apart, and so history entries map to something the edge still recognises.
const newConversationId = () =>
  `c-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

// POLL_MS paces the exchange poll. Fast enough that tool calls appear as they
// happen; slow enough that a long answer costs a handful of requests, not
// hundreds.
const STATUS_RETRY_MS = 6000;
const POLL_MS = 700;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const STARTERS = [
  "What are the features of this Weavian Edge?",
  "What are the intelligence features here?",
  "Why does my video call keep breaking up?",
  "What changed on this box recently?",
];

export default function AssistantWindow() {
  const session = useSession();
  const deepLinkNode = useMemo(() => readNode(), []);
  const [nodeId, setNodeId] = useState(deepLinkNode);
  const [nodes, setNodes] = useState([]);

  /* Resume rather than restart. Ada and the Console are alternative renders of
     one document, so stepping over to a panel and back UNMOUNTS this window —
     a fresh conversation id each time meant the transcript you were reading was
     replaced by an empty one, with the real thing only findable in the history
     list. The id of what this tab was reading is remembered per node instead. */
  const [convId, setConvId] = useState(() => readActive(readNode()) || newConversationId());
  const [paneOpen, setPaneOpen] = useState(true);
  const [status, setStatus] = useState(null);
  const [messages, setMessages] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [diagrams, setDiagrams] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  /* The introduction. The header mark is handed over as the origin so the
     sequence can start on the object the operator is already looking at. */
  const [introOpen, setIntroOpen] = useState(false);
  const markRef = useRef(null);
  const [error, setError] = useState("");

  const scrollRef = useRef(null);
  const taRef = useRef(null);
  const abortRef = useRef(null);
  /* Which proposals and drawings this conversation may show: what already
     existed when it began is someone else's, and what it has claimed since is
     its own. Scoping by IDENTITY rather than by timestamp: the previous
     version compared the edge's clock against the browser's with two seconds
     of slack, which is a guess about two machines nobody keeps in step. And the
     scope is kept WITH the conversation: worked out afresh each time the window
     mounted, it filed the conversation's own cards as someone else's the
     moment the operator came back from the console, and they vanished. */
  const [scope, setScope] = useState(emptyScope);

  const nodeApiUrl = useMemo(() => nodeApiUrlFor(nodeId), [nodeId]);
  const history = useHistory(nodeId);
  const started = messages.length > 0 || busy;
  // The walkthrough in this tab, if any, and how recent ones ended.
  const { tour, ends } = useTour();

  /* ── Node list (multi-node only, and only when none was passed) ── */
  useEffect(() => {
    if (IS_SINGLE_EDGE || deepLinkNode) return;
    fetch(serverApiUrl("/nodes"), { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        const list = (j?.nodes || j || []).map((n) => n.node_id).filter(Boolean);
        setNodes(list);
        setNodeId((cur) => cur || list[0] || "");
      })
      .catch(() => {});
  }, [deepLinkNode]);

  /* ── Status + proposals ── */
  /* `kind` separates three failures that look identical from here and need
     completely different actions: the edge is not answering (wait — an edge
     restart takes seconds), the session has lapsed (log in), or the assistant
     genuinely is not deployed (a config change on the edge and the server).
     Reporting all three as one is how a five-second restart sent someone
     hunting for environment variables. */
  const loadStatus = useCallback(async () => {
    if (!nodeApiUrl("/agent/status")) return;
    try {
      const r = await fetch(nodeApiUrl("/agent/status"), { credentials: "include" });
      if (r.status === 401 || r.status === 403) {
        setStatus({ enabled: false, kind: "auth" });
        return;
      }
      if (!r.ok) { setStatus({ enabled: false, kind: "unreachable" }); return; }
      const j = await r.json();
      setStatus(j.enabled ? j : { ...j, kind: "disabled" });
    } catch {
      setStatus({ enabled: false, kind: "unreachable" });
    }
  }, [nodeApiUrl]);

  const loadProposals = useCallback(async () => {
    if (!nodeApiUrl("/agent/proposals")) return null;
    try {
      const r = await fetch(nodeApiUrl("/agent/proposals"), { credentials: "include" });
      const j = await r.json();
      const list = j.proposals || [];
      setProposals(list);
      return list;
    } catch {
      /* secondary view — keep the last list rather than blanking mid-conversation */
      return null;
    }
  }, [nodeApiUrl]);

  const loadDiagrams = useCallback(async () => {
    if (!nodeApiUrl("/agent/diagrams")) return null;
    try {
      const r = await fetch(nodeApiUrl("/agent/diagrams"), { credentials: "include" });
      const j = await r.json();
      const list = j.diagrams || [];
      setDiagrams(list);
      return list;
    } catch {
      /* same: a transient failure leaves the last set on screen */
      return null;
    }
  }, [nodeApiUrl]);

  useEffect(() => {
    if (!nodeId && !IS_SINGLE_EDGE) return;

    // Pick up the conversation this tab was reading on this edge, if it still
    // exists; otherwise open a fresh one.
    const resumeId = readActive(nodeId);
    const resumed = resumeId ? loadConversation(nodeId, resumeId) : null;
    setConvId(resumed ? resumed.id : newConversationId());
    setMessages(resumed ? resumed.messages || [] : []);

    setProposals([]);
    setDiagrams([]);
    setError("");
    // A resumed conversation keeps the scope it began with; a new one, or one
    // stored before scopes were kept, takes what the appliance holds now.
    setScope(restoreScope(resumed?.scope));
    loadStatus();
    loadProposals().then((list) => setScope((s) => withPrior(s, "proposals", list)));
    loadDiagrams().then((list) => setScope((s) => withPrior(s, "drawings", list)));
  }, [nodeId, loadStatus, loadProposals, loadDiagrams]);

  /* Ada is open, so no walkthrough is running: whichever way the operator
     came back, one under way is paused, and the dock and its card say so. */
  useEffect(() => { pauseTour(); }, []);

  // An edge that was unreachable a moment ago usually is not any more. Keep
  // asking rather than latching a dead window until someone reloads.
  useVisibleInterval(
    () => { if (!busy) loadStatus(); },
    STATUS_RETRY_MS,
    !!status && !status.enabled
  );

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  useEffect(() => () => abortRef.current?.abort(), []);

  /* ── Ask ──
     Start-and-poll, not a stream. An exchange runs tens of seconds, and every
     hop between this browser and the edge — nginx, the console reverse-proxy,
     notabox-server, the anchor's :8091 proxy — will sever a response that long,
     silently and mid-answer. Polling keeps every request short, so no hop's
     timeout is in play and closing the tab costs nothing. Same shape the AS-path
     panel uses, for the same reason. */
  const ask = useCallback(async (question, opts = {}) => {
    const q = question.trim();
    if (!q || busy) return;
    setError("");
    if (!opts.silent) {
      setInput("");
      if (taRef.current) taRef.current.style.height = "auto";
    }
    setBusy(true);
    // A verification turn is a real turn, but the operator did not type it, so
    // it renders as a note about what is happening rather than as their words.
    setMessages((m) => [...m, { role: "user", text: q, note: opts.note }]);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const resp = await fetch(nodeApiUrl("/agent/ask"), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ conversation_id: convId, question: q }),
        signal: controller.signal,
      });
      if (!resp.ok) throw new Error(`edge returned HTTP ${resp.status}`);
      const { exchange_id: id } = await resp.json();
      if (!id) throw new Error("edge did not start an exchange");

      let cursor = 0;
      for (;;) {
        if (controller.signal.aborted) return;
        await sleep(POLL_MS);
        if (controller.signal.aborted) return;

        const pr = await fetch(
          nodeApiUrl(`/agent/exchange/${encodeURIComponent(id)}?since=${cursor}`),
          { credentials: "include", signal: controller.signal }
        );
        if (!pr.ok) throw new Error(`poll failed: HTTP ${pr.status}`);
        const page = await pr.json();

        (page.events || []).forEach(handleEvent);
        cursor = page.next ?? cursor;

        if (page.error) setError(page.error);
        if (page.done) break;
      }
    } catch (e) {
      if (e.name !== "AbortError") setError(e.message || String(e));
    } finally {
      setBusy(false);
      abortRef.current = null;
      loadProposals();
      loadDiagrams();
    }
  }, [busy, convId, nodeApiUrl, loadProposals, loadDiagrams]);

  useEffect(() => { writeActive(nodeId, convId); }, [nodeId, convId]);

  /* Which call in the transcript shows which card, decided once per render:
     the transcript draws from it, and the conversation claims what it shows,
     so what is on screen and what is remembered cannot disagree. */
  const elsewhere = useMemo(() => ownedElsewhere(history.items, convId), [history.items, convId]);
  const assigned = useMemo(
    () => assignArtifacts(messages, proposals, diagrams, scope, elsewhere),
    [messages, proposals, diagrams, scope, elsewhere]
  );
  useEffect(() => { setScope((s) => claim(s, assigned)); }, [assigned]);

  // Persist the transcript whenever it settles, and its scope with it, so a
  // reload, a node switch or a trip to the console loses neither.
  useEffect(() => {
    if (!busy && messages.length) history.remember(convId, messages, storedScope(scope));
    // history.remember is stable per node; messages and scope are the triggers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busy, messages, convId, scope]);

  const handleEvent = (ev) => {
    if (ev.type === "tool") {
      setMessages((m) => [...m, { role: "tool", name: ev.tool, input: ev.input }]);
    } else if (ev.type === "reading") {
      // What that reading actually found, attached to the call that made it.
      setMessages((m) => {
        for (let i = m.length - 1; i >= 0; i--) {
          if (m[i].role === "tool" && m[i].name === ev.tool && !m[i].found) {
            const next = [...m];
            next[i] = { ...next[i], found: ev.summary };
            return next;
          }
        }
        return m;
      });
    } else if (ev.type === "text") {
      setMessages((m) => [...m, { role: "assistant", text: ev.text }]);
    } else if (ev.type === "error") {
      setError(ev.error || "the assistant failed");
    }
  };

  const stop = useCallback(() => abortRef.current?.abort(), []);

  const startNew = useCallback(() => {
    abortRef.current?.abort();
    setConvId(newConversationId());
    setMessages([]);
    setDiagrams([]);
    setError("");
    setScope(beginScope(proposals, diagrams));
  }, [proposals, diagrams]);

  const openConversation = useCallback((c) => {
    abortRef.current?.abort();
    setConvId(c.id);
    setMessages(c.messages || []);
    setError("");
    // Its own cards come back for as long as the appliance still holds them
    // (a drawing half an hour, a change a quarter of one). One stored before
    // scopes were kept maps only what is raised from here on; anything still
    // pending stays reachable after the transcript.
    setScope(c.scope ? restoreScope(c.scope) : beginScope(proposals, diagrams));
    loadDiagrams();
  }, [proposals, diagrams, loadDiagrams]);

  const reset = useCallback(async () => {
    abortRef.current?.abort();
    setMessages([]);
    setDiagrams([]);
    setError("");
    setScope(beginScope(proposals, diagrams));
    try {
      await fetch(nodeApiUrl("/agent/reset"), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ conversation_id: convId }),
      });
    } catch {
      /* history is best-effort; the window is already cleared */
    }
  }, [convId, nodeApiUrl, proposals, diagrams]);

  /* ── Proposals ── */
  const resolveProposal = useCallback(async (id, outcome) => {
    try {
      await fetch(nodeApiUrl(`/agent/proposals/${encodeURIComponent(id)}/resolve`), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: outcome }),
      });
    } finally {
      loadProposals();
    }
  }, [nodeApiUrl, loadProposals]);

  /* Apply performs the write, then RE-READS live state to confirm it. A 200
     is not proof: the request can be absorbed by a proxy, or answered by the
     SPA fallback if the URL resolves to nothing. Returning the verified value
     is what lets the card say "applied" honestly, and returning the real error
     is what stopped this failing invisibly. */
  const applyProposal = useCallback(async (p) => {
    setError("");
    const url = nodeApiUrl(p.path);
    if (!url) throw new Error("No edge selected — cannot apply.");

    const resp = await fetch(url, {
      method: p.method,
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: p.body ? JSON.stringify(p.body) : undefined,
    });
    const detail = await resp.text();
    if (!resp.ok) throw new Error(refusalText(resp.status, detail));
    // The SPA fallback answers 200 with HTML, which would otherwise read as success.
    if (detail.trim().startsWith("<")) {
      throw new Error("The request did not reach the appliance (the page answered instead).");
    }
    // A change the appliance undoes unless confirmed is kept only once it
    // still answers this console.
    if (p.confirm_path) {
      let written = null;
      try { written = JSON.parse(detail); } catch { /* confirmChange says so */ }
      await confirmChange(nodeApiUrl, written, p.current_path, p.confirm_path);
    }

    await resolveProposal(p.id, "applied");

    let confirmed = "";
    if (p.current_path) {
      try {
        const vr = await fetch(nodeApiUrl(p.current_path), { credentials: "include" });
        if (vr.ok) confirmed = findValue(await vr.json(), Object.keys(p.body || {})[0]);
      } catch {
        /* verification is a bonus; the write already succeeded */
      }
    }
    loadProposals();
    return confirmed;
  }, [nodeApiUrl, resolveProposal, loadProposals]);

  /* A card that says "applied" is the window's word for it. The operator asked
     Ada to make the change, so Ada is the one who should confirm it took — with
     her own reading, after the fact, in the conversation where they asked. */
  const confirmApplied = useCallback((applied) => {
    if (!applied.length) return;
    // Name the intended settings, not just the change's title: asked vaguely,
    // she compares against her own earlier reading and can call a real change
    // "no movement". Asked against the target, the check is unambiguous.
    const intended = applied
      .map((p) => `${changeTitle(p.tool)} → ${describeChange(p.body)}`)
      .join("; ");
    const what = applied.length === 1
      ? changeTitle(applied[0].tool)
      : `${applied.length} changes`;

    ask(
      `The operator has just applied this in the console: ${intended}. Take a fresh reading ` +
      `now and confirm in one or two lines whether the appliance actually reflects it, ` +
      `quoting the values you read. If any part did not take, say which and what is still ` +
      `missing. Judge it against those intended settings, not against your earlier readings. ` +
      `Do not propose anything.`,
      { silent: true, note: `Checking that ${what} took effect…` }
    );
  }, [ask]);

  /* Running the sequence for them is the point: a half-applied ladder leaves
     the console showing a state it has no name for, which is exactly what reads
     as "it didn't work". Stops at the first refusal so the operator fixes one
     thing, not three. */
  const [seq, setSeq] = useState({ running: false, at: 0, failed: false });

  const applyAll = useCallback(async (list) => {
    const ordered = list;
    for (let i = 0; i < ordered.length; i++) {
      setSeq({ running: true, at: i + 1, failed: false });
      try {
        await applyProposal(ordered[i]);
      } catch (e) {
        setSeq({ running: false, at: i + 1, failed: true });
        setError(`Step ${i + 1} could not be applied: ${e?.message || e}`);
        return;
      }
    }
    setSeq({ running: false, at: 0, failed: false });
    confirmApplied(ordered);
  }, [applyProposal, confirmApplied]);

  /* A single card applies and confirms on its own. Apply-all confirms once for
     the whole run instead, so a three-step change costs one check, not three. */
  const applyOne = useCallback(async (p) => {
    const out = await applyProposal(p);
    confirmApplied([p]);
    return out;
  }, [applyProposal, confirmApplied]);

  /* A walkthrough that finished is a change that happened. Pick it up on the
     way back in and verify it, rather than leaving the operator looking at a
     card asking them to confirm what they have already done by hand. Read once
     — takeApplied clears it — so returning again does not re-report it. */
  useEffect(() => {
    if (!nodeId && !IS_SINGLE_EDGE) return;
    const applied = takeApplied();
    if (!applied) return;
    loadProposals();
    confirmApplied(applied.map((s) => ({ tool: s.tool, body: s.body })));
    // Runs on arrival only; confirmApplied is stable for this node.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeId]);

  /* A walkthrough the operator stepped away from, by whatever route.
   *
   * Ada holds the way back to it. Without that, stepping in here to ask about a
   * step is the act that ends the walkthrough — which makes the two surfaces
   * mutually exclusive at exactly the moment they are most useful together.
   * Discarding it is somewhere obvious to say "I am done with this": without
   * it the dock is the one thing on screen the operator cannot get rid of.
   */
  const dock = tour && (
    <ResumeDock tour={tour}
      onResume={() => resumeAndOpen(nodeId)}
      onRestart={() => restartAndOpen(nodeId)}
      onDiscard={endTour} />
  );

  /* The question the operator sent themselves from the walkthrough, where the
     locked switch was in front of them and the words for it were the panel's
     rather than theirs. It arrives in the composer to send or edit — offered,
     not asked on their behalf, which is the same rule the tour card follows —
     unless the operator wrote it themselves and asked it from a panel. */
  useEffect(() => {
    const taken = takeTourQuestion();
    if (!taken) return;
    if (taken.send) {
      ask(taken.q);
      return;
    }
    const q = taken.q;
    setInput(q);
    const ta = taRef.current;
    if (ta) {
      ta.focus();
      ta.style.height = "auto";
      ta.style.height = `${Math.min(ta.scrollHeight, 220)}px`;
    }
  }, [ask]);

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      ask(input);
    }
  };

  const grow = (e) => {
    setInput(e.target.value);
    const el = e.target;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 220)}px`;
  };

  // Ordered by what the appliance will accept, not by when Ada happened to
  // raise each one — a foundation proposed last still has to be applied first,
  // and Apply-all, the step numbers and the walkthrough must all agree on that.
  const pending = useMemo(
    () => orderChanges(proposals.filter((p) => p.status === "pending")),
    [proposals]
  );

  /* The walkthrough is the alternative to Apply, not a companion to it: the
     operator makes the change themselves with the control highlighted, which is
     the half that teaches them where it lives. Hand over the whole sequence so
     the tour spans panels if the change does. */
  const showMe = useCallback((from) => {
    const rest = from ? pending.slice(pending.findIndex((p) => p.id === from.id)) : pending;
    const steps = buildTour(rest);
    if (!steps.length) return;
    if (!startTour(steps, { kind: "change", id: rest[0].id })) {
      setError("This browser will not keep a walkthrough — use Apply instead.");
      return;
    }
    navigateTo(VIEW_CONSOLE, { node: nodeId, panel: steps[0].panel, from: "console" });
  }, [pending, nodeId]);

  /* Where the walkthrough's own state is said, once: in the step banner when
     there is one, else on the first of its changes still waiting. Every card
     in it says which step it is. */
  const walk = useMemo(() => {
    const changes = walkthroughOf(tour, { ids: pending.map((p) => p.id) });
    if (!changes || changes.origin?.kind === "tour") return { tour: null, at: null };
    const mine = pending.filter((p) => changes.steps.some((s) => s.id === p.id));
    const banner = pending.length > 1 && assigned.proposals.has(pending[0].id);
    const at = banner ? "banner" : (mine.find((p) => p.id === changes.origin?.id) || mine[0]).id;
    return { tour: changes, at };
  }, [tour, pending, assigned]);


  return (
    <div className="aw-root">
      <Styles />
      <div className="aw-horizon" aria-hidden="true" />

      <aside className={`aw-rail ${paneOpen ? "" : "aw-rail-closed"}`}>
        <div className="aw-rail-top">
          <button className="aw-newchat" onClick={startNew}>
            <FiPlus size={13} /> New conversation
          </button>
        </div>
        <div className="aw-rail-list">
          {history.items.length === 0 && (
            <p className="aw-rail-empty">Past conversations appear here.</p>
          )}
          {history.items.map((c) => (
            <div key={c.id} className={`aw-rail-item ${c.id === convId ? "aw-rail-on" : ""}`}>
              <button className="aw-rail-open" onClick={() => openConversation(c)} title={c.title}>
                <FiMessageSquare size={12} />
                <span className="aw-rail-title">{c.title}</span>
                <span className="aw-rail-when">{relativeTime(c.at)}</span>
              </button>
              <button
                className="aw-rail-del"
                onClick={() => history.remove(c.id)}
                title="Delete this conversation"
                aria-label="Delete conversation"
              >
                <FiTrash2 size={11} />
              </button>
            </div>
          ))}
        </div>
      </aside>

      <div className="aw-surface">
      <header className="aw-nav">
        <button
          className="aw-railtoggle"
          onClick={() => setPaneOpen((v) => !v)}
          title={paneOpen ? "Hide history" : "Show history"}
          aria-label="Toggle history"
        >
          <FiSidebar size={15} />
        </button>
        <button
          className="aw-brand"
          onClick={() => navigateTo(VIEW_LANDING)}
          title="Back to the interface chooser"
        >
          <span className="aw-mark" aria-hidden="true">
            <img src={`${process.env.PUBLIC_URL}/sidebar-logo-w.png`} alt="" />
          </span>
          <span className="aw-brandname">{ASSISTANT_NAME}</span>
        </button>

        <div className="aw-navcenter">
          {!IS_SINGLE_EDGE && nodes.length > 0 ? (
            <select className="aw-select" value={nodeId} onChange={(e) => setNodeId(e.target.value)}>
              {nodes.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          ) : (
            <span className="aw-chip">{IS_SINGLE_EDGE ? "this device" : nodeId || "no edge"}</span>
          )}
          {pending.length > 0 && (
            <span className="aw-chip aw-chip-alert">
              {pending.length} awaiting you
            </span>
          )}
        </div>

        <div className="aw-navright">
          {started && (
            <button className="aw-btn" onClick={reset}>
              <FiRefreshCw size={13} /> New
            </button>
          )}
          <button
            className="aw-btn aw-btn-primary"
            onClick={() => navigateTo(VIEW_CONSOLE, { node: nodeId, from: "console" })}
          >
            {CONSOLE_NAME} <FiExternalLink size={13} />
          </button>
        </div>
      </header>

      {status && !status.enabled ? (
        <main className="aw-center">
          <div className="aw-notice">
            {status.kind === "unreachable" ? (
              <>
                <div className="aw-notice-head"><FiAlertTriangle /> This edge is not answering</div>
                <p>
                  {ASSISTANT_NAME} runs on the appliance itself, so it is out of reach until the
                  edge is back. A restart takes a few seconds.
                </p>
                <p className="aw-notice-dim aw-notice-retry">
                  <span className="aw-spin aw-spin-dim" /> Still trying — this will clear itself.
                </p>
              </>
            ) : status.kind === "auth" ? (
              <>
                <div className="aw-notice-head"><FiAlertTriangle /> Your session has expired</div>
                <p>Sign in again and {ASSISTANT_NAME} will pick up where you left off.</p>
                <button className="aw-notice-btn"
                        onClick={() => navigateTo(VIEW_CONSOLE, { node: nodeId, from: "console" })}>
                  Go to {CONSOLE_NAME}
                </button>
              </>
            ) : (
              <>
                <div className="aw-notice-head"><FiAlertTriangle /> {ASSISTANT_NAME} is not running on this box</div>
                <p>
                  Boxes with current software run {ASSISTANT_NAME} on their own, on your organisation's
                  Anthropic account: an administrator adds that key under Administration, Assistant.
                </p>
                {status.reason && <p className="aw-notice-dim">The box says: {status.reason}</p>}
                {!IS_SINGLE_EDGE && session.isTenantAdmin && (
                  <button className="aw-notice-btn"
                          onClick={() => navigateTo(VIEW_CONSOLE, { node: nodeId, panel: "assistant", from: "console" })}>
                    Open Administration, Assistant
                  </button>
                )}
              </>
            )}
          </div>
        </main>
      ) : !started ? (
        /* ── Empty state: hero + input, centred ── */
        <main className="aw-center">
          <button
            className="aw-meet"
            onClick={() => setIntroOpen(true)}
            title={`A short introduction to ${ASSISTANT_NAME}`}
          >
            <span className="aw-meet-sheen" aria-hidden="true" />
            <span className="aw-meet-orb" aria-hidden="true" />
            <span className="aw-meet-label">Meet&nbsp;{ASSISTANT_NAME}</span>
          </button>
          <h1 className="aw-hero">
            {/* Hidden, not unmounted, while the introduction runs: the stand-in
                is sitting exactly on top of it and two would show. */}
            <span className="aw-mark" aria-hidden="true" ref={markRef}
                  style={introOpen ? { opacity: 0 } : undefined}>
              <img src={`${process.env.PUBLIC_URL}/sidebar-logo-w.png`} alt="" />
            </span>
            <span className="aw-hero-text">Ask {ASSISTANT_NAME}</span>
          </h1>
          <p className="aw-sub">Diagnose, explain, and tune Weavian Edge in plain language</p>
          {dock}
          <Composer
            value={input} onChange={grow} onKeyDown={onKeyDown}
            onSend={() => ask(input)} busy={busy} onStop={stop} taRef={taRef}
            placeholder="Ask why a call is choppy, which link is busiest, what changed…"
          />
          <div className="aw-starters">
            {STARTERS.map((s) => (
              <button key={s} className="aw-starter" onClick={() => ask(s)}>{s}</button>
            ))}
          </div>

          {/* A change queued in an earlier conversation lives on the appliance,
              not in this window, so opening a fresh one used to leave the header
              counting changes that were nowhere on screen. */}
          {pending.length > 0 && (
            <div className="aw-waiting">
              <div className="aw-waiting-head">
                {pending.length === 1 ? "One change is waiting for you" : `${pending.length} changes are waiting for you`}
              </div>
              {pending.map((p) => (
                <ProposalCard
                  key={p.id}
                  proposal={p}
                  onApply={() => applyOne(p)}
                  onDismiss={() => resolveProposal(p.id, "rejected")}
                  onShowMe={() => showMe(p)}
                  walk={walk} ends={ends}
                  nodeId={nodeId}
                  stale
                />
              ))}
            </div>
          )}
        </main>
      ) : (
        /* ── Conversation ── */
        <main className="aw-convo">
          <div className="aw-stream" ref={scrollRef}>
            {renderTranscript(
              messages, assigned, applyOne, resolveProposal,
              { nodeId, pending, seq, busy, walk, ends, onApplyAll: () => applyAll(pending), onShowMe: showMe }
            )}

            {/* Anything pending the transcript did not place — raised in another
                conversation, or one this browser never saw. It sits at the END
                because the stream scrolls to the bottom, and a card above the
                fold is a card nobody sees. */}
            {orphanPending(pending, assigned).map((p) => (
              <ProposalCard
                key={p.id}
                proposal={p}
                onApply={() => applyOne(p)}
                onDismiss={() => resolveProposal(p.id, "rejected")}
                onShowMe={() => showMe(p)}
                walk={walk} ends={ends}
                nodeId={nodeId}
                stale
              />
            ))}

            {error && <div className="aw-error">{error}</div>}
          </div>
          <div className="aw-dock">
            {dock}
            <Composer
              value={input} onChange={grow} onKeyDown={onKeyDown}
              onSend={() => ask(input)} busy={busy} onStop={stop} taRef={taRef}
              placeholder="Ask a follow-up…"
            />
          </div>
        </main>
      )}
      </div>

      {/* Mounted only while it runs, so it measures the header mark where the
          mark actually is on the day it is opened. */}
      {introOpen && <AdaIntro originRef={markRef} onClose={() => setIntroOpen(false)} />}
    </div>
  );
}

/* ── Composer ── */

function Composer({ value, onChange, onKeyDown, onSend, busy, onStop, taRef, placeholder }) {
  return (
    <div className="aw-composer">
      <textarea
        ref={taRef}
        className="aw-input"
        rows={1}
        value={value}
        onChange={onChange}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        spellCheck={false}
      />
      {busy ? (
        <button className="aw-send aw-send-stop" onClick={onStop} title="Stop">
          <FiSquare size={13} />
        </button>
      ) : (
        <button className="aw-send" onClick={onSend} disabled={!value.trim()} title="Send">
          <FiArrowUp size={16} />
        </button>
      )}
    </div>
  );
}

/* renderTranscript places each diagram where it was actually drawn, replacing
   the draw_ tool trace, so Ada's "the diagram above" refers to something the
   operator can see in that spot. Diagrams arrive oldest-first and map onto the
   draw_ calls in order; until one has loaded, its tool line stands in. */
// orphanPending returns proposals still awaiting a decision that this
// transcript does not show inline — raised before a reload, or in a
// conversation that has since been reopened. Without this they would be
// unreachable; with it they are not duplicated either.
/* The appliance refuses a change whose foundation is missing, and says why in
   terms of its own internals. That text is not for an operator: it explains
   mechanism, and mechanism is not something this product discusses. So a
   refusal is translated into what the operator should DO about it, and the
   original never reaches the screen. */
function refusalText(status, raw) {
  if (status === 401) return "Your session has expired. Sign in again, then apply this.";
  if (status === 403) return "This account has read-only access. An operator has to apply this.";
  if (status === 404) return "This appliance is not reachable right now.";

  const body = (raw || "").toLowerCase();
  if (status === 409) {
    if (body.includes("lane is not running")) {
      return "Nothing is encrypted yet, so there is nothing to conceal. Arm at least one class in the Encryption panel first, then apply this.";
    }
    if (body.includes("readable wire")) {
      return "The concealed wire has to be on before this layer. Turn on wire obfuscation in the Obfuscation panel, then apply this.";
    }
    if (body.includes("refusing to disable")) {
      return "Another layer is still using this. Turn those off first, then apply this.";
    }
    if (body.includes("already fixes")) {
      return "Constant-rate is holding the timing already, so this layer would do nothing. Turn constant-rate off first if you want this instead.";
    }
    return "This cannot be applied while the appliance is in its current state.";
  }
  if (status === 400) return "The appliance did not accept this change.";
  return `The change did not go through (${status}).`;
}

// findValue looks for a key in a fresh reading so the card can show the value
// that is now live. Searches one level deep because state is often nested under
// its subsystem. Returns "" when it cannot be found, and the card simply omits
// the confirmation rather than guessing.
function findValue(doc, key) {
  if (!doc || !key) return "";
  if (Object.prototype.hasOwnProperty.call(doc, key)) return String(doc[key]);
  for (const v of Object.values(doc)) {
    if (v && typeof v === "object" && !Array.isArray(v) &&
        Object.prototype.hasOwnProperty.call(v, key)) {
      return String(v[key]);
    }
  }
  return "";
}

// Which artifact each call shows is decided before rendering, by
// assignArtifacts (tour/proposalMatch.js); this only draws it.
function renderTranscript(messages, assigned, onApply, onResolve, ctx) {
  const out = [];
  let run = [];

  // A complex question takes twenty readings. Listing each on its own line
  // buries the answer under the working, so consecutive readings collapse into
  // one line that expands when someone actually wants the provenance.
  const flushRun = (key, live = false) => {
    if (!run.length) return;
    // Mid-answer the operator wants to see it working, not a tally of work
    // already done. The same run collapses to that tally once it finishes.
    out.push(live
      ? <LiveSteps key={`live-${key}`} tools={run} />
      : <ToolRun key={`run-${key}`} tools={run} />);
    run = [];
  };

  messages.forEach((m, i) => {
    if (isDrawingCall(m)) {
      flushRun(i);
      const a = assigned.at.get(i);
      if (!a) { out.push(<ToolCall key={i} message={m} />); return; }
      out.push(
        a.kind === "answer" ? <TieredAnswer key={a.id} artifact={a} />
        : a.kind === "tour" ? <TourCard key={a.id} artifact={a} nodeId={ctx.nodeId} />
        : <DiagramCard key={a.id} diagram={a} />
      );
      return;
    }
    // A change lands where it was proposed, so Apply is next to the sentence
    // that explains it rather than scrolled off the top of the transcript.
    if (isProposalCall(m)) {
      flushRun(i);
      const p = assigned.at.get(i);
      if (!p) { out.push(<ToolCall key={i} message={m} />); return; }
      const steps = ctx.pending.length;
      const step = ctx.pending.findIndex((q) => q.id === p.id) + 1;
      if (step === 1 && steps > 1) {
        out.push(<StepBanner key={`steps-${p.id}`} steps={steps}
          onApplyAll={ctx.onApplyAll}
          onShowMe={ctx.onShowMe ? () => ctx.onShowMe(null) : undefined}
          walk={ctx.walk.at === "banner" ? ctx.walk.tour : null} nodeId={ctx.nodeId}
          running={ctx.seq.running} at={ctx.seq.at} failed={ctx.seq.failed} />);
      }
      out.push(<ProposalCard key={p.id} proposal={p} nodeId={ctx.nodeId}
        step={step} steps={steps} walk={ctx.walk} ends={ctx.ends}
        onShowMe={ctx.onShowMe ? () => ctx.onShowMe(p) : undefined}
        onApply={() => onApply(p)} onDismiss={() => onResolve(p.id, "rejected")} />);
      return;
    }
    if (m.role === "tool") { run.push(m); return; }
    flushRun(i);
    out.push(<Message key={i} message={m} />);
  });
  const hadTrailing = run.length > 0;
  flushRun("end", ctx.busy);
  // Thinking before the first call, or writing after the last one: still show
  // that something is happening.
  if (ctx.busy && !hadTrailing) out.push(<LiveSteps key="live-idle" tools={[]} />);
  return out;
}

/* What it is doing, as it does it. Completed steps stay listed so the operator
   can see the shape of the answer forming rather than a spinner that says
   nothing for thirty seconds.

   The current step also carries a clock. The longest wait in an exchange is a
   single model call, and nothing arrives during one — the transport is
   deliberately non-streaming (see exchange.go), so between one reading landing
   and the next event there is genuinely nothing new to say. A line that has sat
   unchanged for half a minute reads as hung; the same line with the seconds
   ticking reads as working. It restarts whenever a step completes, so it always
   times the step on screen rather than the whole answer. */
/* One value out of a reading's arguments — WHAT she chose to look at.
 *
 * Which class, which link, which anchor is the only part of the reasoning
 * visible from outside the model, and it is the difference between "Reading how
 * each class is routed" eight times over and watching her work through them one
 * at a time. Deliberately one short value: a hint beside the step, not the call
 * dumped on screen. The full arguments stay where they were, behind the
 * collapsed provenance row. */
function stepDetail(input) {
  if (!input || typeof input !== "object" || Array.isArray(input)) return "";
  for (const v of Object.values(input)) {
    if (typeof v === "string" && v.length > 0 && v.length <= 22) return v;
    if (typeof v === "number" && isFinite(v)) return String(v);
  }
  return "";
}

const SHOW_CLOCK_AFTER = 5;

function LiveSteps({ tools }) {
  const [secs, setSecs] = useState(0);
  const done = tools.length;

  useEffect(() => {
    const startedAt = Date.now();
    setSecs(0);
    const t = setInterval(
      () => setSecs(Math.round((Date.now() - startedAt) / 1000)),
      1000
    );
    return () => clearInterval(t);
  }, [done]);

  return (
    <div className="aw-live">
      {tools.map((t, i) => (
        <div className="aw-live-row" key={i}>
          <FiCheck size={12} className="aw-live-tick" />
          <span>{stepLabel(t.name)}</span>
          {stepDetail(t.input) && <em className="aw-live-arg">{stepDetail(t.input)}</em>}
          {t.found && <em className="aw-live-found">{t.found}</em>}
        </div>
      ))}
      <div className="aw-live-row is-now">
        <span className="aw-live-dot" />
        <span>{done ? "Working out what that means" : "Reading the appliance"}</span>
        {/* What it is working FROM — the one honest detail available while a
            model call is in flight. */}
        {done > 1 && <em className="aw-live-from">from {done} readings</em>}
        {secs >= SHOW_CLOCK_AFTER && <em className="aw-live-clock">{secs}s</em>}
      </div>
    </div>
  );
}

function ToolRun({ tools }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="aw-run">
      <button className="aw-runhead" onClick={() => setOpen((o) => !o)}>
        {open ? <FiChevronDown size={11} /> : <FiChevronRight size={11} />}
        <FiTool size={11} />
        {tools.length === 1 ? tools[0].name : `${tools.length} readings taken`}
      </button>
      {open && (
        <div className="aw-runlist">
          {tools.map((t, i) => <ToolCall key={i} message={t} />)}
        </div>
      )}
    </div>
  );
}

/* ── Messages ── */

function Message({ message }) {
  if (message.role === "tool") return <ToolCall message={message} />;
  if (message.role === "user") {
    // Shown as a note, not as a quote, when the window raised the turn itself.
    if (message.note) {
      return (
        <div className="aw-note">
          <FiRefreshCw size={12} /> {message.note}
        </div>
      );
    }
    return <div className="aw-user"><div className="aw-user-bubble">{message.text}</div></div>;
  }
  return <div className="aw-assistant">{renderMarkdownish(message.text)}</div>;
}

/* Tool calls stay visible. Someone acting on the answer should be able to see
   which readings it came from. */
function ToolCall({ message }) {
  const [open, setOpen] = useState(false);
  const hasArgs = message.input && Object.keys(message.input).length > 0;
  return (
    <div className="aw-tool">
      <button className="aw-toolhead" onClick={() => hasArgs && setOpen((o) => !o)}>
        {hasArgs ? (open ? <FiChevronDown size={11} /> : <FiChevronRight size={11} />) : <i />}
        <FiTool size={11} />
        <code>{message.name}</code>
      </button>
      {open && hasArgs && <pre className="aw-toolargs">{JSON.stringify(message.input, null, 2)}</pre>}
    </div>
  );
}

/* ── Proposal ── */

/* A change awaiting confirmation.
 *
 * It shows WHAT changes in the operator's language and nothing about how the
 * change is delivered. An earlier version printed the HTTP method, the API path
 * and the raw response body as a before/after — which leaked the internal API
 * shape and field names to whoever was looking at the screen, and was useless
 * besides: thirty internal fields to explain a one-field change. Ada's text
 * explains the reasoning; this card is the confirmation.
 */

// Tool name to something an operator recognises.
/* Where a change becomes visible. A change the operator cannot SEE land is
   indistinguishable from one that failed, so every proposal points at the panel
   that shows its effect and offers to open it. Longest prefix wins. */
const PANEL_FOR = [
  ["/obf", "obfuscation"],
  ["/policy/encryption", "encryption"],
  ["/policy", "traffic"],
  ["/qos", "qos"],
  ["/aqm", "aqm"],
  ["/tc_wan_priority", "wan-priority"],
  ["/apply_tc_wan_priority", "wan-priority"],
  ["/cloud", "cloud"],
  ["/health", "health"],
  ["/classifier", "traffic"],
  ["/diagnostics/episodes", "continuity"],
  ["/diagnostics/flows", "continuity"],
  ["/diagnostics", "diag-overview"],
  ["/dataset", "dataset"],
];

function panelFor(path) {
  let best = null;
  for (const [prefix, key] of PANEL_FOR) {
    if ((path || "").startsWith(prefix) && (!best || prefix.length > best[0].length)) {
      best = [prefix, key];
    }
  }
  return best ? { key: best[1], label: navLabel(best[1]) } : null;
}

/* A multi-step change is the case that confused people: apply one of three and
   the console still reads what it read before, because a half-applied change is
   not a state the panel has a name for. So a sequence announces its length up
   front and offers to run the whole thing. */
function StepBanner({ steps, onApplyAll, onShowMe, walk, nodeId, running, at, failed }) {
  if (steps < 2) return null;
  return (
    <div className="aw-steps">
      <div className="aw-steps-row">
        <div className="aw-steps-txt">
          <strong>This takes {steps} steps.</strong>{" "}
          {failed
            ? `Stopped at step ${at}. Fix that one, then continue.`
            : running
              ? `Applying step ${at} of ${steps}…`
              : "The console will not show the new setting until all of them are applied."}
        </div>
        <div className="aw-steps-acts">
          <button className="aw-steps-btn" disabled={running} onClick={onApplyAll}>
            {running ? <span className="aw-spin aw-spin-dark" /> : <FiCheck size={14} />}
            {running ? "Applying…" : `Apply all ${steps} steps`}
          </button>
          {onShowMe && !walk && (
            <button className="aw-steps-guide" disabled={running} onClick={onShowMe}>
              <FiCompass size={14} /> Walk me through it
            </button>
          )}
        </div>
      </div>
      {walk && <TourProgress tour={walk} nodeId={nodeId} />}
    </div>
  );
}

/* The whole plan of a walkthrough the operator stepped away from, docked with
   the composer: being walked through something means seeing what is behind
   you and what is left. Coming here for one question used to collapse all of
   that to a single line, so the operator arrived back with no idea which of
   six steps they had been standing on. */
function ResumeDock({ tour, onResume, onRestart, onDiscard }) {
  return (
    <div className="aw-resume">
      <div className="aw-resume-head">
        <FiCompass size={14} />
        <span className="aw-resume-title">Your walkthrough is paused</span>
        <span className="aw-resume-pos">step {tour.at + 1} of {tour.steps.length}</span>
        <button className="aw-resume-x" onClick={onDiscard}
                title="Discard this walkthrough" aria-label="Discard this walkthrough">
          <FiX size={14} />
        </button>
      </div>

      <ol className="aw-resume-steps">
        {tour.steps.map((s, i) => (
          <li key={s.id} className={i < tour.at ? "is-done" : i === tour.at ? "is-now" : ""}>
            <span className="aw-resume-mark">{i < tour.at ? <FiCheck size={10} /> : i + 1}</span>
            <span className="aw-resume-step">{s.title}</span>
            {i === tour.at && <span className="aw-resume-here">you are here</span>}
          </li>
        ))}
      </ol>

      <div className="aw-resume-acts">
        <button className="aw-resume-go" onClick={onResume}>
          Pick up where you left off
          <FiChevronRight size={14} />
        </button>
        <button className="aw-resume-again" onClick={onRestart}>
          <FiRotateCcw size={13} /> Start over
        </button>
      </div>
    </div>
  );
}

/* What a card says about the walkthrough its change belongs to: which step it
   is while one is paused, and afterwards whether the appliance showed it done. */
function walkWords(proposal, walk, ends, done) {
  const at = walk?.tour ? walk.tour.steps.findIndex((s) => s.id === proposal.id) : -1;
  if (!done && at >= 0) {
    return `Step ${at + 1} of ${walk.tour.steps.length} in your paused walkthrough`;
  }
  const end = endingOf(ends, { ids: [proposal.id] });
  const result = end && end.steps.find((s) => s.id === proposal.id);
  if (!result || result.done === null) return "";
  if (done && proposal.status === "applied" && result.done) {
    return "Done in the walkthrough, and the appliance shows it";
  }
  if (!done && !result.done) return "The walkthrough ended with this not set";
  return "";
}

function ProposalCard({ proposal, onApply, onDismiss, onShowMe, stale, step, steps, nodeId, walk, ends }) {
  const [state, setState] = useState("idle"); // idle | applying | applied | failed
  const [failure, setFailure] = useState("");
  const [live, setLive] = useState("");

  const busy = state === "applying";
  const done = state === "applied" ||
    (proposal.status && proposal.status !== "pending" && state === "idle");
  // A change in the paused walkthrough is shown there, not started afresh.
  const walked = !done && !!walk?.tour?.steps.some((s) => s.id === proposal.id);
  const walkNote = walkWords(proposal, walk, ends, done);

  const doApply = async () => {
    setState("applying");
    setFailure("");
    try {
      const confirmed = await onApply();
      setLive(confirmed || "");
      setState("applied");
    } catch (e) {
      // Surfaced in the card, not at the bottom of the stream, because that is
      // where the operator is looking when they press the button.
      setFailure(e?.message || String(e));
      setState("failed");
    }
  };

  const doDismiss = async () => {
    setState("applying");
    try { await onDismiss(); setState("idle"); } catch { setState("idle"); }
  };

  const title = stepTitle(proposal);
  const fields = Object.entries(proposal.body || {});
  const panel = panelFor(proposal.path);
  const last = !steps || step === steps;

  return (
    <div className={`aw-prop ${stale ? "aw-prop-stale" : ""} ${done ? "aw-prop-done" : ""}`}>
      <div className="aw-prop-top">
        <div className="aw-prop-id">
          <div className="aw-prop-title">
            {steps > 1 && <span className="aw-prop-step">Step {step} of {steps}</span>}
            {title}
          </div>
          <div className="aw-prop-sub">
            {state === "applying" ? "Applying to the appliance…"
              : state === "applied"
                ? (steps > 1 && !last
                    ? `Applied. ${steps - step} step${steps - step > 1 ? "s" : ""} still to go.`
                    : live ? `Applied — now ${live}` : "Applied")
              : state === "failed" ? "Could not apply"
              : walkNote || (done ? (proposal.status === "applied" ? "Applied" : "Dismissed")
                : stale ? "Still waiting on you"
                : "Waiting for your confirmation")}
          </div>
        </div>
        {!done && (
          <div className="aw-prop-actions">
            <button className="aw-apply" disabled={busy} onClick={doApply}>
              {busy ? <span className="aw-spin" /> : <FiCheck size={15} />}
              {busy ? "Applying…" : (state === "failed" ? "Retry" : "Apply")}
            </button>
            {onShowMe && !walked && (
              <button className="aw-showme" disabled={busy} onClick={onShowMe}
                      title="Highlight this control in the console and walk you through it">
                <FiCompass size={14} /> Show me
              </button>
            )}
            <button className="aw-dismiss" disabled={busy} onClick={doDismiss}>
              <FiX size={13} /> Dismiss
            </button>
          </div>
        )}
        {done && (
          <span className={`aw-prop-badge ${proposal.status === "applied" ? "ok" : ""}`}>
            {proposal.status === "applied" ? <FiCheck size={13} /> : <FiX size={13} />}
            {proposal.status}
          </span>
        )}
      </div>

      {fields.length > 0 && (
        <dl className="aw-prop-fields">
          {fields.map(([k, v]) => (
            <div key={k}>
              {/* Named and valued as the panel does, so the card and the
                  control it points at do not read as two different settings. */}
              <dt>{humanLabel(k, proposal.tool)}</dt>
              <dd>{humanValue(v, k, proposal.tool)}</dd>
            </div>
          ))}
        </dl>
      )}

      {walked && walk.at === proposal.id && <TourProgress tour={walk.tour} nodeId={nodeId} />}

      {done && panel && (
        <button
          className="aw-prop-see"
          onClick={() => navigateTo(VIEW_CONSOLE, { node: nodeId, panel: panel.key, from: "console" })}
        >
          <FiExternalLink size={13} /> See it in {panel.label}
        </button>
      )}

      {state === "failed" && (
        <p className="aw-prop-fail">
          <FiAlertTriangle size={13} /> {failure}
        </p>
      )}

      {!done && state !== "failed" && (
        <p className="aw-prop-note">
          Nothing has changed yet. Applying makes the change on this appliance under your account.
        </p>
      )}
    </div>
  );
}


/* ── Styles ──
   Scoped with an aw- prefix and shipped with the component so this window does
   not inherit the dashboard's chrome. Palette is cyan → indigo → violet on
   near-black: a signal palette rather than the dashboard's slate. */
function Styles() {
  return (
    <style>{`
:root { color-scheme: dark; }
.aw-root {
  --bg:#07080B; --fg:#E8EAF2; --dim:#8A90A6; --line:rgba(255,255,255,.09);
  --cyan:#38BDF8; --indigo:#818CF8; --violet:#C084FC;
  position:fixed; inset:0; background:var(--bg); color:var(--fg);
  font-family:'Inter',ui-sans-serif,system-ui,-apple-system,sans-serif;
  display:flex; flex-direction:column; overflow:hidden;
}
.aw-root *{box-sizing:border-box}
.aw-root{flex-direction:row}
.aw-surface{flex:1;display:flex;flex-direction:column;min-width:0;position:relative;z-index:1}

/* History rail */
.aw-rail{
  position:relative;z-index:3;width:258px;flex:none;display:flex;flex-direction:column;
  border-right:1px solid var(--line);background:rgba(0,0,0,.34);backdrop-filter:blur(8px);
  transition:width .2s cubic-bezier(.4,0,.2,1),opacity .2s;overflow:hidden;
}
.aw-rail-closed{width:0;opacity:0;border-right-color:transparent}
.aw-rail-top{padding:13px 12px;border-bottom:1px solid var(--line)}
.aw-newchat{
  width:100%;display:flex;align-items:center;justify-content:center;gap:7px;
  font:inherit;font-size:12.5px;font-weight:500;color:var(--fg);cursor:pointer;
  padding:9px 12px;border-radius:9px;border:1px solid rgba(129,140,248,.34);
  background:linear-gradient(135deg,rgba(56,189,248,.15),rgba(192,132,252,.17));
  transition:border-color .15s,transform .15s;
}
.aw-newchat:hover{border-color:rgba(129,140,248,.6);transform:translateY(-1px)}
.aw-rail-list{flex:1;overflow-y:auto;padding:8px}
.aw-rail-empty{margin:12px 6px;font-size:12px;color:#5F6479;line-height:1.5}
.aw-rail-item{display:flex;align-items:stretch;border-radius:8px;transition:background .15s}
.aw-rail-item:hover{background:rgba(255,255,255,.05)}
.aw-rail-on{background:rgba(129,140,248,.14)}
.aw-rail-open{
  flex:1;min-width:0;display:grid;grid-template-columns:auto 1fr;gap:4px 7px;
  align-items:center;text-align:left;background:none;border:0;cursor:pointer;
  font:inherit;color:#C7CCDC;padding:9px 6px 9px 10px;
}
.aw-rail-title{
  font-size:12.5px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;min-width:0;
}
.aw-rail-when{grid-column:2;font-size:10.5px;color:#5F6479}
.aw-rail-on .aw-rail-title{color:#fff}
.aw-rail-del{
  flex:none;background:none;border:0;cursor:pointer;color:#5F6479;padding:0 10px;
  opacity:0;transition:opacity .15s,color .15s;
}
.aw-rail-item:hover .aw-rail-del{opacity:1}
.aw-rail-del:hover{color:#F87171}
.aw-railtoggle{
  background:none;border:0;cursor:pointer;color:var(--dim);padding:4px 8px 4px 0;
  display:flex;align-items:center;transition:color .15s;
}
.aw-railtoggle:hover{color:var(--fg)}
@media (max-width:820px){
  .aw-rail{position:absolute;inset:0 auto 0 0;height:100%}
}

/* Horizon glow — the planet-limb arc, violet core bleeding to cyan. */
.aw-horizon{
  position:absolute; left:50%; transform:translateX(-50%);
  bottom:-58vh; width:190vw; height:112vh; pointer-events:none; z-index:0;
  background:
    radial-gradient(closest-side, rgba(129,140,248,.50), rgba(129,140,248,0) 72%),
    radial-gradient(closest-side, rgba(192,132,252,.34), rgba(192,132,252,0) 62%),
    radial-gradient(closest-side, rgba(56,189,248,.26), rgba(56,189,248,0) 82%);
  filter:blur(26px);
}
.aw-horizon::after{
  content:""; position:absolute; inset:0;
  background:radial-gradient(closest-side, rgba(7,8,11,.92), rgba(7,8,11,0) 58%);
}

/* Nav */
.aw-nav{
  position:relative; z-index:2; display:flex; align-items:center;
  justify-content:space-between; gap:16px; padding:14px 22px;
  border-bottom:1px solid var(--line); backdrop-filter:blur(8px);
}
.aw-brand{
  display:flex;align-items:center;gap:10px;min-width:190px;
  background:none;border:0;padding:0;color:inherit;cursor:pointer;font:inherit;
}
.aw-brand:hover .aw-brandname{color:#fff}
/* The ring is drawn on ::before so the mark can sit inside it — masking the
   element itself would take the logo out with the middle of the gradient. */
.aw-mark{
  position:relative;flex:none;
  width:var(--mark,26px);height:var(--mark,26px);
  display:grid;place-items:center;
}
.aw-mark::before{
  content:"";position:absolute;inset:0;border-radius:50%;
  background:conic-gradient(from 210deg,var(--cyan),var(--indigo),var(--violet),var(--cyan));
  -webkit-mask:radial-gradient(circle, transparent 57%, #000 60%);
          mask:radial-gradient(circle, transparent 57%, #000 60%);
}
.aw-mark img{position:relative;width:50%;height:50%;object-fit:contain;display:block}
.aw-brandname{font-size:15px;font-weight:600;letter-spacing:-.01em;transition:color .15s}
.aw-navcenter{display:flex;align-items:center;gap:8px;flex:1;justify-content:center}
.aw-navright{display:flex;align-items:center;gap:8px;min-width:190px;justify-content:flex-end}

/* "Meet Ada" — the invitation, sitting directly above the hero.
   A gradient edge on a dark pill rather than a filled one: it sits inches above
   a wordmark that is already a full-strength gradient, and two of those in the
   same eyeline flatten each other. This one glows; the wordmark stays the
   brightest thing on the screen. */
.aw-meet{
  position:relative;overflow:hidden;display:inline-flex;align-items:center;gap:10px;
  margin:0 0 26px;padding:10px 22px 10px 17px;border-radius:999px;cursor:pointer;
  font:inherit;font-size:14.5px;font-weight:600;letter-spacing:.01em;color:#EEF0FA;
  border:1px solid transparent;
  background:
    linear-gradient(#0C0E15,#0C0E15) padding-box,
    linear-gradient(120deg,var(--cyan),var(--indigo) 52%,var(--violet)) border-box;
  box-shadow:0 8px 30px -12px rgba(129,140,248,.9);
  transition:transform .18s,box-shadow .18s,filter .18s;
}
.aw-meet:hover{
  transform:translateY(-1px);filter:brightness(1.07);
  box-shadow:0 14px 40px -12px rgba(192,132,252,.95);
}
.aw-meet:active{transform:translateY(0)}
.aw-meet:focus-visible{outline:2px solid #A5B4FC;outline-offset:3px}
.aw-meet-label{position:relative;z-index:1;white-space:nowrap}
.aw-meet-orb{
  position:relative;z-index:1;flex:none;width:11px;height:11px;border-radius:50%;
  background:conic-gradient(from 210deg,var(--cyan),var(--indigo),var(--violet),var(--cyan));
  box-shadow:0 0 12px 1px rgba(129,140,248,.8);
  animation:aw-meet-turn 5.5s linear infinite;
}
@keyframes aw-meet-turn{from{transform:rotate(0)}to{transform:rotate(360deg)}}
.aw-meet-sheen{
  position:absolute;top:0;left:-150%;width:60%;height:100%;
  background:linear-gradient(100deg,transparent,rgba(255,255,255,.28),transparent);
  animation:aw-meet-sheen 5.4s ease-in-out infinite;
}
.aw-meet:hover .aw-meet-sheen{animation-play-state:paused}
@keyframes aw-meet-sheen{0%{left:-150%}48%{left:160%}100%{left:160%}}
@media (prefers-reduced-motion:reduce){
  .aw-meet-sheen,.aw-meet-orb{animation:none}
}
.aw-chip{
  font-size:12px;color:var(--dim);border:1px solid var(--line);
  padding:4px 10px;border-radius:999px;background:rgba(255,255,255,.03);white-space:nowrap;
}
.aw-chip-alert{color:#C7D2FE;border-color:rgba(129,140,248,.45);background:rgba(129,140,248,.12)}
.aw-select{
  font:inherit;font-size:12px;color:var(--fg);background:rgba(255,255,255,.04);
  border:1px solid var(--line);border-radius:999px;padding:4px 10px;outline:none;
}
.aw-btn{
  display:inline-flex;align-items:center;gap:6px;font-size:12.5px;color:var(--fg);
  background:rgba(255,255,255,.05);border:1px solid var(--line);border-radius:999px;
  padding:6px 13px;cursor:pointer;text-decoration:none;transition:background .15s,border-color .15s;
}
.aw-btn:hover{background:rgba(255,255,255,.09)}
.aw-btn-primary{
  background:linear-gradient(135deg,rgba(56,189,248,.20),rgba(192,132,252,.22));
  border-color:rgba(129,140,248,.42);
}

/* Empty state */
.aw-center{
  position:relative;z-index:1;flex:1;display:flex;flex-direction:column;
  align-items:center;justify-content:center;padding:24px;gap:14px;overflow:auto;
}
/* The heading is the flex row; the gradient lives on the text inside it.
   Keeping background-clip:text off the container matters — it is what clips a
   replaced child out of existence in some engines, and the mark is one. */
.aw-hero{
  margin:0;font-size:clamp(38px,5.6vw,68px);font-weight:700;letter-spacing:-.035em;
  line-height:1.05;display:flex;align-items:center;justify-content:center;gap:.26em;
}
.aw-hero-text{
  background:linear-gradient(100deg,var(--cyan) 8%,var(--indigo) 48%,var(--violet) 88%);
  -webkit-background-clip:text;background-clip:text;color:transparent;
}
/* Sized off the type so the ring tracks the clamped heading at every width, and
   optically aligned rather than centred on the line box — the cap height of the
   text sits above its own centre. */
.aw-hero .aw-mark{
  --mark:1.02em;margin-top:-.05em;
  filter:drop-shadow(0 6px 26px rgba(129,140,248,.45));
}
.aw-sub{margin:0 0 12px;color:var(--dim);font-size:15px;text-align:center}
.aw-starters{display:flex;flex-wrap:wrap;gap:8px;justify-content:center;max-width:760px;margin-top:4px}
.aw-starter{
  font-size:12.5px;color:#B9C0D4;background:rgba(255,255,255,.04);
  border:1px solid var(--line);border-radius:999px;padding:7px 14px;cursor:pointer;
  transition:background .15s,color .15s,border-color .15s;
}
.aw-starter:hover{background:rgba(129,140,248,.14);color:#fff;border-color:rgba(129,140,248,.4)}

/* The way back into a paused walkthrough. Docked with the composer because it
   is the other thing the operator can do from here, and it has to be visible
   without scrolling the transcript. */
.aw-resume{
  width:min(1180px,100%);margin-bottom:10px;text-align:left;
  border:1px solid rgba(103,232,249,.34);border-radius:16px;
  background:linear-gradient(180deg,rgba(103,232,249,.09),rgba(129,140,248,.05));
  padding:13px 15px 12px;
}
.aw-resume-head{display:flex;align-items:center;gap:9px;color:#67E8F9}
.aw-resume-title{font-size:13.5px;font-weight:700;line-height:1;color:#E2E8F0}
.aw-resume-pos{
  font-size:10.5px;font-weight:700;line-height:1;letter-spacing:.06em;text-transform:uppercase;
  color:#BAE6FD;background:rgba(56,189,248,.16);border-radius:6px;padding:4px 8px;
}
.aw-resume-x{
  margin-left:auto;background:none;border:0;color:#5F6479;cursor:pointer;
  padding:3px;display:grid;place-items:center;border-radius:6px;
}
.aw-resume-x:hover{color:#CBD5E1;background:rgba(255,255,255,.06)}

.aw-resume-steps{
  list-style:none;margin:11px 0 0;padding:0;display:flex;flex-wrap:wrap;gap:6px;
}
/* A list of where the walkthrough stands, not things to press: no frames,
   the current step tinted. */
.aw-resume-steps li{
  display:inline-flex;align-items:center;gap:7px;min-width:0;
  border-radius:999px;padding:5px 11px 5px 5px;
  font-size:12.5px;font-weight:500;line-height:1;color:#7A8199;
}
.aw-resume-steps li.is-done{color:#94A3B8}
.aw-resume-steps li.is-now{
  color:#F1F5F9;font-weight:650;background:rgba(103,232,249,.12);
}
.aw-resume-mark{
  width:18px;height:18px;border-radius:50%;display:grid;place-items:center;flex:none;
  background:rgba(255,255,255,.07);color:#8891AC;font-size:10.5px;font-weight:700;
}
.aw-resume-steps li.is-done .aw-resume-mark{background:rgba(52,211,153,.18);color:#6EE7B7}
.aw-resume-steps li.is-now .aw-resume-mark{background:rgba(103,232,249,.24);color:#67E8F9}
.aw-resume-step{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.aw-resume-here{
  font-size:9.5px;font-weight:700;line-height:1;letter-spacing:.07em;text-transform:uppercase;
  color:#0B0C11;background:#67E8F9;border-radius:999px;padding:3px 7px;flex:none;
}

.aw-resume-acts{display:flex;gap:8px;margin-top:11px}
.aw-resume-go,.aw-resume-again{
  cursor:pointer;display:inline-flex;align-items:center;justify-content:center;gap:8px;
  border-radius:11px;padding:10px 16px;font:inherit;font-size:13.5px;font-weight:700;line-height:1;
}
.aw-resume-go{flex:1;border:0;color:#0B0C11;background:linear-gradient(135deg,#67E8F9,#818CF8)}
.aw-resume-go:hover{filter:brightness(1.06)}
.aw-resume-again{flex:none;border:1px solid rgba(148,163,184,.34);background:transparent;color:#CBD5E1}
.aw-resume-again:hover{border-color:rgba(103,232,249,.6);color:#67E8F9}

/* Composer */
.aw-composer{
  position:relative;width:min(1180px,100%);
  border-radius:18px;border:1px solid rgba(129,140,248,.34);
  background:rgba(18,19,26,.86);
  box-shadow:0 0 0 1px rgba(0,0,0,.4),0 18px 60px -18px rgba(129,140,248,.42);
  transition:border-color .18s,box-shadow .18s;
}
.aw-composer:focus-within{
  border-color:rgba(129,140,248,.72);
  box-shadow:0 0 0 1px rgba(0,0,0,.4),0 22px 70px -16px rgba(129,140,248,.60);
}
.aw-input{
  width:100%;background:transparent;border:0;outline:none;resize:none;color:var(--fg);
  font:inherit;font-size:15px;line-height:1.55;padding:18px 60px 18px 20px;
  min-height:92px;max-height:220px;
}
.aw-input::placeholder{color:#5F6479}
.aw-send{
  position:absolute;right:12px;bottom:12px;width:34px;height:34px;border-radius:50%;
  display:grid;place-items:center;cursor:pointer;border:0;color:#0B0C11;
  background:linear-gradient(135deg,var(--cyan),var(--indigo));
  transition:opacity .15s,transform .15s;
}
.aw-send:hover:not(:disabled){transform:translateY(-1px)}
.aw-send:disabled{opacity:.32;cursor:default}
.aw-send-stop{background:rgba(255,255,255,.16);color:var(--fg)}

/* Conversation */
.aw-convo{position:relative;z-index:1;flex:1;display:flex;flex-direction:column;min-height:0}
/* Sized against the window beside the history rail, not the viewport: at
   96vw, with the rail open, the stream ran under the window's right edge and
   took the right-hand buttons of every card with it. */
.aw-stream{
  flex:1;overflow-y:auto;padding:28px 28px 8px;display:flex;flex-direction:column;gap:16px;
  width:min(1180px,100%);margin:0 auto
}
/* Children of a flex COLUMN shrink by default, so a tall card gets squashed and
   silently clipped mid-content. Nothing in this stream should ever compress. */
.aw-stream > *{flex:none}
.aw-dock{padding:12px 20px 22px;display:flex;flex-direction:column;align-items:center}
.aw-user{display:flex;justify-content:flex-end}
.aw-user-bubble{
  max-width:70%;background:linear-gradient(135deg,rgba(56,189,248,.16),rgba(129,140,248,.20));
  border:1px solid rgba(129,140,248,.30);border-radius:16px 16px 4px 16px;
  padding:11px 15px;font-size:14.5px;line-height:1.5;
}
.aw-assistant{font-size:14.5px;line-height:1.68;color:#DDE1EC;max-width:100%}
.aw-assistant p{margin:0 0 10px}
.aw-assistant code{
  background:rgba(255,255,255,.07);border-radius:5px;padding:1px 5px;
  font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:12.5px;color:#A5B4FC;
}
.aw-assistant strong{color:#fff;font-weight:600}

/* Headings. The first one in an answer must not push off the top, so its
   top margin collapses away. */
.aw-h1,.aw-h2,.aw-h3,.aw-h4{
  margin:22px 0 10px;font-weight:600;letter-spacing:-.01em;line-height:1.3;color:#fff;
}
.aw-assistant > :first-child{margin-top:0}
.aw-h1{font-size:20px}
.aw-h2{
  font-size:17.5px;padding-bottom:6px;border-bottom:1px solid var(--line);
}
.aw-h3{font-size:15.5px;color:#C7D2FE}
.aw-h4{font-size:14px;color:#A5B4FC;text-transform:uppercase;letter-spacing:.06em}
.aw-hr{border:0;border-top:1px solid var(--line);margin:18px 0}

.aw-ul,.aw-ol{margin:0 0 12px;padding-left:4px;list-style:none}
.aw-ol{counter-reset:awitem}
.aw-ul li,.aw-ol li{position:relative;margin:6px 0;padding-left:22px;line-height:1.6}
.aw-ul li::before{
  content:"";position:absolute;left:6px;top:.62em;
  width:5px;height:5px;border-radius:50%;background:var(--indigo);
}
.aw-ol li{counter-increment:awitem}
.aw-ol li::before{
  content:counter(awitem);position:absolute;left:0;top:0;
  font-size:11.5px;font-weight:600;color:var(--indigo);
  font-variant-numeric:tabular-nums;line-height:1.9;
}

/* Tables scroll inside their own box so a wide one never widens the answer. */
.aw-tablewrap{overflow-x:auto;margin:2px 0 14px;max-width:100%}
.aw-table{border-collapse:collapse;font-size:13.5px;width:100%}
.aw-table th,.aw-table td{border:1px solid var(--line);padding:8px 12px;text-align:left;white-space:nowrap}
.aw-table td:last-child,.aw-table th:last-child{white-space:normal}
.aw-table th{
  background:rgba(255,255,255,.05);font-weight:600;color:#C7CCDC;
  font-size:11.5px;text-transform:uppercase;letter-spacing:.06em;
}
.aw-table tbody tr:nth-child(even){background:rgba(255,255,255,.018)}

/* Tool trace */
.aw-tool{font-size:12px;color:#6F758B}
.aw-toolhead{
  display:flex;align-items:center;gap:6px;background:none;border:0;color:inherit;
  cursor:pointer;padding:0;font:inherit;
}
.aw-toolhead i{width:11px;display:inline-block}
.aw-toolhead:hover{color:#A5B4FC}
.aw-toolargs{
  margin:5px 0 0 22px;padding:9px 11px;background:rgba(255,255,255,.03);
  border:1px solid var(--line);border-radius:9px;font-size:11.5px;color:#8A90A6;overflow-x:auto;
}
.aw-run{font-size:12px}
.aw-runhead{
  display:flex;align-items:center;gap:6px;background:none;border:0;cursor:pointer;
  font:inherit;font-size:12px;color:#6F758B;padding:0;transition:color .15s;
}
.aw-runhead:hover{color:#A5B4FC}
.aw-runlist{margin:6px 0 0 18px;display:flex;flex-direction:column;gap:4px}

.aw-thinking{display:flex;align-items:center;gap:5px;color:#6F758B;font-size:13px}
.aw-thinking span{
  width:5px;height:5px;border-radius:50%;background:var(--indigo);
  animation:awpulse 1.2s infinite ease-in-out;
}
.aw-thinking span:nth-child(2){animation-delay:.16s}
.aw-thinking span:nth-child(3){animation-delay:.32s;margin-right:6px}
@keyframes awpulse{0%,80%,100%{opacity:.25}40%{opacity:1}}
.aw-error{
  border:1px solid rgba(248,113,113,.42);background:rgba(248,113,113,.10);
  color:#FCA5A5;border-radius:10px;padding:10px 13px;font-size:13px;
}

/* Proposal */
.aw-prop{
  border:1px solid rgba(129,140,248,.55);border-radius:14px;padding:16px 18px;
  background:linear-gradient(180deg,rgba(129,140,248,.15),rgba(129,140,248,.05));
  box-shadow:0 10px 34px -18px rgba(129,140,248,.75);
}
.aw-prop-stale{opacity:.9;border-style:dashed}
/* Three actions plus a title do not fit one row at every width, and the stream
   clips horizontally — which is how "Show me" went missing on narrower windows
   rather than wrapping. Let the header reflow instead. */
.aw-prop-id{min-width:0;flex:1 1 200px}
.aw-prop-top{display:flex;justify-content:space-between;gap:12px 14px;align-items:flex-start;flex-wrap:wrap}
.aw-prop-title{font-size:15px;font-weight:650;color:#fff;letter-spacing:-.01em}
.aw-prop-sub{font-size:12.5px;color:#A5B4FC;margin-top:3px}
.aw-prop-done{border-style:solid;border-color:rgba(255,255,255,.14);background:rgba(255,255,255,.03);box-shadow:none}
.aw-prop-badge{
  display:inline-flex;align-items:center;gap:6px;flex-shrink:0;font-size:12px;
  padding:6px 12px;border-radius:999px;border:1px solid var(--line);
  color:#8A90A6;text-transform:capitalize;
}
.aw-steps{
  margin:16px 0 10px;
  border:1px solid rgba(129,140,248,.42);background:rgba(129,140,248,.11);
  border-radius:12px;padding:14px 16px;
}
.aw-steps-row{display:flex;align-items:center;gap:16px;flex-wrap:wrap}
.aw-steps-txt{flex:1 1 260px;font-size:13.5px;line-height:1.55;color:#C7D2FE}
.aw-steps-txt strong{color:#E0E7FF}
.aw-steps-btn{
  flex:none;display:inline-flex;align-items:center;gap:7px;cursor:pointer;
  border:0;border-radius:9px;padding:9px 15px;font:inherit;font-size:13.5px;font-weight:600;line-height:1;
  background:linear-gradient(135deg,#67E8F9,#818CF8);color:#0B0C11;
}
.aw-steps-btn:disabled{opacity:.6;cursor:default}
.aw-prop-step{
  display:inline-block;margin-right:9px;padding:2px 8px;border-radius:6px;
  background:rgba(129,140,248,.20);color:#C7D2FE;
  font-size:11px;font-weight:700;letter-spacing:.04em;text-transform:uppercase;
  vertical-align:middle;
}
.aw-steps-acts{display:flex;align-items:center;gap:9px;flex:none;flex-wrap:wrap}
.aw-steps-guide{
  display:inline-flex;align-items:center;gap:7px;cursor:pointer;
  border:1px solid rgba(129,140,248,.55);background:transparent;border-radius:9px;
  padding:8px 14px;font:inherit;font-size:13.5px;font-weight:600;line-height:1;color:#C7D2FE;
}
.aw-steps-guide:hover:not(:disabled){border-color:#818CF8;color:#E0E7FF}
.aw-steps-guide:disabled{opacity:.55;cursor:default}
.aw-showme{
  display:inline-flex;align-items:center;gap:6px;cursor:pointer;
  border:1px solid rgba(129,140,248,.55);background:transparent;border-radius:9px;
  padding:8px 13px;font:inherit;font-size:13px;font-weight:600;line-height:1;color:#C7D2FE;
}
.aw-showme:hover:not(:disabled){border-color:#818CF8;color:#E0E7FF}
.aw-notice-retry{display:flex;align-items:center;gap:9px;margin-top:14px}
.aw-spin-dim{border-color:rgba(148,163,184,.25);border-top-color:#94A3B8}
.aw-notice-btn{
  margin-top:14px;display:inline-flex;align-items:center;cursor:pointer;
  border:1px solid rgba(103,232,249,.5);background:transparent;border-radius:9px;
  padding:9px 15px;font:inherit;font-size:13.5px;font-weight:600;line-height:1;color:#67E8F9;
}
.aw-notice-btn:hover{background:rgba(103,232,249,.1)}
.aw-waiting{
  width:100%;max-width:760px;margin:34px auto 0;display:flex;flex-direction:column;gap:12px;
  text-align:left;
}
.aw-waiting-head{
  font-size:12px;font-weight:700;letter-spacing:.07em;text-transform:uppercase;color:#A5B4FC;
}
.aw-live{
  display:flex;flex-direction:column;gap:9px;margin:4px 0 2px;
  border-left:2px solid rgba(129,140,248,.35);padding:2px 0 2px 14px;
}
.aw-live-row{
  display:flex;align-items:center;gap:9px;font-size:13.5px;color:#6B7392;
  animation:awfade .25s ease-out both;
}
.aw-live-row.is-now{color:#C7D2FE}
.aw-live-tick{color:#4C5678;flex:none}
/* What the reading returned. Dimmer than the step itself: the step is what is
   happening, this is what it found, and the second should not shout. */
.aw-live-found{
  font-style:normal;color:#818CF8;font-size:12.5px;
  padding:1px 8px;border-radius:999px;background:rgba(129,140,248,.12);
}
.aw-live-dot{
  width:7px;height:7px;border-radius:50%;flex:none;background:#818CF8;
  animation:awpulse 1.1s ease-in-out infinite;
}
/* What the step was ASKED about. Quieter than the step and quieter than what
   it found — it is context for the line, not the line itself. */
.aw-live-arg{
  font-style:normal;color:#8891AC;font-size:12px;
  padding:1px 7px;border-radius:999px;border:1px solid rgba(148,163,184,.16);
  max-width:16ch;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;flex:none;
}
.aw-live-from{font-style:normal;color:#5B6280;font-size:12px;flex:none}
/* The clock sits at the far end and is the dimmest thing on the row: it is
   there to prove the wait is a wait, not to be read. */
.aw-live-clock{
  font-style:normal;color:#4C5678;font-size:11.5px;margin-left:auto;flex:none;
  font-variant-numeric:tabular-nums;
}
@keyframes awfade{from{opacity:0;transform:translateY(3px)}to{opacity:1;transform:none}}
@keyframes awpulse{0%,100%{opacity:.35;transform:scale(.8)}50%{opacity:1;transform:scale(1)}}
@media (prefers-reduced-motion:reduce){
  .aw-live-row{animation:none}
  .aw-live-dot{animation-duration:2.4s}
}
.aw-note{
  display:flex;align-items:center;gap:8px;align-self:flex-start;
  margin:2px 0;padding:7px 12px;border-radius:999px;
  border:1px dashed rgba(148,163,184,.35);background:rgba(148,163,184,.06);
  font-size:12.5px;color:#94A3B8;
}
.aw-prop-see{
  margin-top:13px;display:inline-flex;align-items:center;gap:7px;cursor:pointer;
  border:1px solid rgba(148,163,184,.38);background:transparent;border-radius:9px;
  padding:8px 13px;font:inherit;font-size:13px;font-weight:600;line-height:1;color:#CBD5E1;
}
.aw-prop-see:hover{border-color:rgba(103,232,249,.6);color:#67E8F9}
.aw-spin-dark{border-color:rgba(255,255,255,.35);border-top-color:#0B0C11}
.aw-prop-fail{
  margin:13px 0 0;display:flex;align-items:flex-start;gap:8px;
  border:1px solid rgba(248,113,113,.45);background:rgba(248,113,113,.10);
  border-radius:9px;padding:10px 13px;font-size:13px;color:#FCA5A5;line-height:1.5;
}
.aw-spin{
  width:13px;height:13px;border-radius:50%;flex:none;
  border:2px solid rgba(11,12,17,.35);border-top-color:#0B0C11;
  animation:awspin .7s linear infinite;
}
@keyframes awspin{to{transform:rotate(360deg)}}
@media (prefers-reduced-motion:reduce){.aw-spin{animation-duration:2s}}
.aw-prop-badge.ok{color:#6EE7B7;border-color:rgba(110,231,183,.35);background:rgba(110,231,183,.10)}
.aw-prop-fields{
  margin:14px 0 0;display:grid;grid-template-columns:auto 1fr;gap:8px 18px;align-items:baseline;
}
.aw-prop-fields dt{
  font-size:11px;letter-spacing:.07em;text-transform:uppercase;color:#8A90A6;white-space:nowrap;
}
.aw-prop-fields dd{margin:0;font-size:14.5px;font-weight:600;color:#fff}
.aw-prop-actions{display:flex;gap:8px;flex-wrap:wrap;flex-shrink:0}
.aw-apply{
  display:inline-flex;align-items:center;gap:7px;border:0;border-radius:9px;cursor:pointer;
  padding:11px 20px;font-size:14px;font-weight:700;color:#0B0C11;letter-spacing:-.01em;
  background:linear-gradient(135deg,#22D3EE,#818CF8);
  box-shadow:0 6px 18px -6px rgba(56,189,248,.8);
  transition:transform .15s,box-shadow .15s,filter .15s;
}
.aw-apply:hover:not(:disabled){transform:translateY(-1px);filter:saturate(1.1);box-shadow:0 10px 24px -6px rgba(129,140,248,.9)}
.aw-apply:active:not(:disabled){transform:translateY(0)}
.aw-apply:focus-visible{outline:2px solid #A5B4FC;outline-offset:3px}
.aw-dismiss{
  display:inline-flex;align-items:center;gap:5px;border:1px solid var(--line);
  border-radius:8px;cursor:pointer;padding:7px 13px;font-size:12.5px;
  color:#C7CCDC;background:rgba(255,255,255,.04);
}
.aw-apply:disabled,.aw-dismiss:disabled{opacity:.5;cursor:default}
.aw-prop-note{margin:9px 0 0;font-size:11.5px;color:#9AA0B4}

/* Notice */
.aw-notice{
  max-width:560px;border:1px solid rgba(251,191,36,.34);background:rgba(251,191,36,.07);
  border-radius:14px;padding:18px 20px;font-size:14px;line-height:1.6;
}
.aw-notice-head{display:flex;align-items:center;gap:8px;font-weight:600;color:#FCD34D;margin-bottom:8px}
.aw-notice p{margin:0 0 8px}
.aw-notice-dim{color:var(--dim);font-size:13px}
.aw-notice code{
  background:rgba(255,255,255,.07);border-radius:4px;padding:1px 5px;
  font-family:ui-monospace,monospace;font-size:12px;color:#A5B4FC;
}
`}</style>
  );
}
